import {EventEmitter, Injectable} from '@angular/core';
import {WhiteboardService} from '@app/modules/whiteboard/services/whiteboard.service';
import {WebSocketService} from '@app/modules/shared/services/web-socket.service';
import {UtilService} from '@app/modules/shared/services/util.service';
import {HttpClient} from '@angular/common/http';
import {Store} from '@ngxs/store';
import {PrivateWhiteboard, PrivateWhiteboardAction, PrivateWhiteboardState} from '@app/state/private-whiteboard.state';
import {MessageRecord, MsgRecordType, RecordArgs} from '@app/services/data.service';
import {WhiteboardEvents, WhiteboardStates} from '@app/models/commands/whiteboard-commands';
import {PrivateWhiteboardCommands} from '@app/models/commands/private-whiteboard-commands';
import {Container} from '@app/modules/svg/Container';
import {PointerTool} from '@app/modules/svg/service/tool/pointer/PointerTool';

@Injectable({
  providedIn: 'root'
})
export class PrivateWhiteboardService extends WhiteboardService {
  protected override _whiteboard: PrivateWhiteboard;

  public onInit: EventEmitter<PrivateWhiteboard> = new EventEmitter();

  protected override readonly teacherBroadcast: string = '/app/private-whiteboard/t';
  protected override readonly teacherPrivate: string = '/app/private-whiteboard/tp';
  protected override readonly publicBroadcast: string = '/app/private-whiteboard/b';
  protected override readonly undoDestination: string = `/app/private-whiteboard/undo`;
  protected override readonly redoDestination: string = `/app/private-whiteboard/redo`;
  protected override readonly purgeDestination: string = `/app/private-whiteboard/purge`;

  protected override readonly addDestination: string = `/app/private-whiteboard/add`;
  protected override readonly subDestination: string = `/app/private-whiteboard/sub`;
  protected override readonly delDestination: string = `/app/private-whiteboard/del`;

  protected override readonly getStatePath: string = '/private-whiteboard/get-state';

  constructor(
      protected webSocketService: WebSocketService,
      protected utilService: UtilService,
      protected http: HttpClient,
      protected store: Store
  ) {
    super(webSocketService, utilService, http, store);
  }

  protected override dispatchContainerState(container: Container): void {
    this.store.dispatch(new PrivateWhiteboardAction.SetContainer(container));
  }

  protected override dispatchWrapper(wrapper: HTMLDivElement): void {
    this.store.dispatch(new PrivateWhiteboardAction.SetWrapper(wrapper));
  }

  protected override dispatchStaffPointerTool(staffPointerTool: PointerTool): void {
    this.store.dispatch(new PrivateWhiteboardAction.SetStaffPointerTool(staffPointerTool));
  }

  protected override dispatchStaffPointerCount(count: number): void {
    this.store.dispatch(new PrivateWhiteboardAction.SetStaffPointersCount(count));
  }

  protected override dispatchBroadcastScroll(broadcast: boolean): void {
    this.store.dispatch(new PrivateWhiteboardAction.SetBroadcastScroll(broadcast));
  }

  protected override dispatchStreamingState(streaming: boolean): void {
    this.store.dispatch(new PrivateWhiteboardAction.SetStreaming(streaming));
  }

  protected override dispatchSmoothState(smooth: boolean): void {
    this.store.dispatch(new PrivateWhiteboardAction.SetSmooth(smooth));
  }

  protected override dispatchHeight(height: number): void {
    this.store.dispatch(new PrivateWhiteboardAction.SetHeight(height));
  }

  protected override dispatchWidth(width: number): void {
    this.store.dispatch(new PrivateWhiteboardAction.SetWidth(width));
  }

  protected override dispatchScrollPosition(position: number): void {
    this.store.dispatch(new PrivateWhiteboardAction.SetScrollPosition(position));
  }

  protected override dispatchLastPostedProblemId(id: number): void {
    this.store.dispatch(new PrivateWhiteboardAction.SetLastPostedProblemId(id));
  }

  protected override dispatchShareWithAll(): void {
    this.store.dispatch(new PrivateWhiteboardAction.ShareWithAll());
  }

  protected override dispatchShareWith(id: number): void {
    this.store.dispatch(new PrivateWhiteboardAction.ShareWith(id));
  }

  protected override dispatchUnshareWithAll(): void {
    this.store.dispatch(new PrivateWhiteboardAction.UnshareWithAll());
  }

  protected override dispatchUnshareWith(id: number): void {
    this.store.dispatch(new PrivateWhiteboardAction.UnshareWith(id));
  }

  protected override listenWhiteboardStateChanges(): void {
    this.store.select(PrivateWhiteboardState).subscribe((whiteboard: PrivateWhiteboard) => {
      this._whiteboard = whiteboard;
    });
  }

  protected override dispatchReset(): void {
    this.store.dispatch(new PrivateWhiteboardAction.Reset());
  }

  public override buildRecord(command: string, args: RecordArgs): MessageRecord {
      const msgType = WhiteboardStates[command] ? MsgRecordType.STATE : WhiteboardEvents[command] ? MsgRecordType.EVENT : null;
      return {
          category: PrivateWhiteboardCommands.category,
          name: command,
          data: args.data,
          type: msgType,
          ownerId: null, /* must be set server-side on receive */
          targetId: args.targetId
      };
  }
}
